import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

const AuthContext = createContext(null);

export const ROLES = {
    SUPERADMIN: 99,
    ORG_ADMIN: 98,
    MANAGER: 97,
    EMPLOYEE: 96
};

const ROLE_LABELS = {
    99: 'Superadmin',
    98: 'Org Admin',
    97: 'Manager',
    96: 'Employee'
};

export const AuthProvider = ({ children }) => {
    const [currentUser, setCurrentUser] = useState(() => {
        try {
            const stored = localStorage.getItem('sv_user');
            if (stored) return JSON.parse(stored);
            return null;
        } catch (e) {
            return null;
        }
    });

    const roleId = currentUser ? Number(currentUser.RoleID) : null;
    const empId = currentUser ? Number(currentUser.empId) : null;
    const teamMemberIds = currentUser?.teamMemberIds || [];

    const isSuper = roleId === ROLES.SUPERADMIN;
    const isOrgAdmin = roleId === ROLES.ORG_ADMIN;
    const isManager = roleId === ROLES.MANAGER;
    const isEmployee = roleId === ROLES.EMPLOYEE;
    const isAdmin = isSuper || isOrgAdmin;
    const canDelete = isSuper || isOrgAdmin || isManager;
    const roleLabel = ROLE_LABELS[roleId] || (isSuper ? 'Superadmin' : isOrgAdmin ? 'Org Admin' : isManager ? 'Manager' : 'Employee');

    // Organization Scope Management (for Superadmin)
    const [activeOrgId, setActiveOrgIdState] = useState(() => {
        if (!isSuper) return currentUser?.OrgID ? String(currentUser.OrgID) : '1';
        return localStorage.getItem('sv_active_org_id') || '1';
    });

    const [activeOrgName, setActiveOrgName] = useState(() => {
        if (!isSuper) return currentUser?.OrgName || 'CodexterLabs';
        return localStorage.getItem('sv_active_org_name') || 'CodexterLabs';
    });

    // Update active org if user changes or is non-superadmin
    useEffect(() => {
        if (currentUser && !isSuper) {
            const userOrg = currentUser.OrgID ? String(currentUser.OrgID) : '1';
            setActiveOrgIdState(userOrg);
            setActiveOrgName(currentUser.OrgName || 'Organization');
            localStorage.setItem('sv_active_org_id', userOrg);
            localStorage.setItem('sv_active_org_name', currentUser.OrgName || 'Organization');
        }
    }, [currentUser, isSuper]);

    const setActiveOrg = useCallback((orgId, orgName = '') => {
        const idStr = String(orgId);
        setActiveOrgIdState(idStr);
        localStorage.setItem('sv_active_org_id', idStr);
        if (orgName) {
            setActiveOrgName(orgName);
            localStorage.setItem('sv_active_org_name', orgName);
        } else if (idStr === 'all') {
            setActiveOrgName('All Organizations');
            localStorage.setItem('sv_active_org_name', 'All Organizations');
        }
        // Dispatch custom event to notify components to refresh data
        window.dispatchEvent(new CustomEvent('sv_active_org_changed', { detail: { orgId: idStr, orgName } }));
    }, []);

    const login = useCallback((userData, token) => {
        if (token) {
            localStorage.setItem('token', token);
            localStorage.setItem('sv_token', token);
        }
        localStorage.setItem('sv_user', JSON.stringify(userData));
        setCurrentUser(userData);

        if (Number(userData.RoleID) !== ROLES.SUPERADMIN) {
            const org = userData.OrgID ? String(userData.OrgID) : '1';
            localStorage.setItem('sv_active_org_id', org);
            localStorage.setItem('sv_active_org_name', userData.OrgName || 'Organization');
            setActiveOrgIdState(org);
            setActiveOrgName(userData.OrgName || 'Organization');
        }
    }, []);

    const logout = useCallback(() => {
        localStorage.removeItem('token');
        localStorage.removeItem('sv_token');
        localStorage.removeItem('sv_user');
        localStorage.removeItem('sv_active_org_id');
        localStorage.removeItem('sv_active_org_name');
        setCurrentUser(null);
        window.location.href = '/login';
    }, []);

    const updateUser = useCallback((updatedData) => {
        const merged = { ...currentUser, ...updatedData };
        localStorage.setItem('sv_user', JSON.stringify(merged));
        setCurrentUser(merged);
    }, [currentUser]);

    // In "All Organizations" mode, creating records is disabled
    const canWrite = !isSuper || (activeOrgId && activeOrgId !== 'all');

    // Build query params for backward compatibility
    const getRbacParams = () => ({
        roleId: roleId,
        empId: empId,
        teamMemberIds: JSON.stringify(teamMemberIds),
        orgId: activeOrgId
    });

    return (
        <AuthContext.Provider value={{
            currentUser,
            login,
            logout,
            updateUser,
            roleId,
            empId,
            teamMemberIds,
            isSuper,
            isOrgAdmin,
            isManager,
            isEmployee,
            isAdmin,
            canDelete,
            roleLabel,
            activeOrgId,
            activeOrgName,
            setActiveOrg,
            canWrite,
            getRbacParams
        }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuth must be used within AuthProvider');
    return ctx;
};

export default AuthContext;
